import { Injectable, DestroyRef, signal, Signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgHttpCachingService } from 'ng-http-caching';
import { catchError, combineLatest, concatMap, map, Observable, of, tap } from 'rxjs';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import {
	CreateOrganizationsDto,
	GetMyCompaniesResponse,
	OrganizationsApiService,
	StrictHttpResponse,
	OrganizationList
} from '@shure/cloud/shared/switch-organization/data-access';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import { PrismDialogRef, PrismDialogService, PrismSnackbarService } from '@shure/prism-angular-components';
import { asLabel } from '@shure/shared/models';

import { ChangeOrganizationDialogComponent } from '../ui/change-organization-dialog/change-organization-dialog.component';
import { CreateOrganizationDialogComponent } from '../ui/create-organization/create-organization-dialog.component';

@Injectable({
	providedIn: 'root'
})
export class SwitchOrganizationService {
	public readonly defaultDialogConfig: MatDialogConfig = {
		width: '447px',
		panelClass: 'sh-custom-dialog-container',
		restoreFocus: false
	};
	private reInitiateAddLicenses = signal(false);

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private prismDialogService: PrismDialogService,
		private dialog: MatDialog,
		private apiOrganizationsService: OrganizationsApiService,
		private snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService,
		private readonly oktaIntfService: OktaInterfaceService,
		private readonly destroyRef: DestroyRef,
		private ngHttpCachingService: NgHttpCachingService,
		private apiUsersService: ApiUsersService,
		private orgStoreService: OrganizationsStoreService
	) {}

	/**
	 * Opens the 'Create Organization' dialog.
	 * This method opens a modal dialog for creating an organization using `MatDialog`.
	 */
	public openCreateDialog(): MatDialogRef<CreateOrganizationDialogComponent, MatDialogConfig> {
		return this.dialog.open(CreateOrganizationDialogComponent, this.defaultDialogConfig);
	}

	/**
	 * Opens the 'Change Organization' dialog.
	 * This method opens a modal dialog for changing an organization using `MatDialog`.
	 */
	public openChangeDialog(code?: string): MatDialogRef<ChangeOrganizationDialogComponent, MatDialogConfig> {
		return this.dialog.open(ChangeOrganizationDialogComponent, {
			...this.defaultDialogConfig,
			data: code
		});
	}

	/**
	 * Opens the Prism dialog for creating a new organization.
	 *
	 * @returns A reference to the opened CreateOrganizationDialogComponent instance.
	 */
	public openCreateOrgDialog(): PrismDialogRef<CreateOrganizationDialogComponent> {
		return this.prismDialogService.openDialog(CreateOrganizationDialogComponent);
	}

	/**
	 * Opens the Prism dialog for changing the organization.
	 * Optionally accepts a code to pass as dialog data.
	 *
	 * @param code - Optional string used to initialize dialog context.
	 * @returns A reference to the opened ChangeOrganizationDialogComponent instance.
	 */
	public openChangeOrgDialog(code?: string): PrismDialogRef<ChangeOrganizationDialogComponent> {
		return this.prismDialogService.openDialog(ChangeOrganizationDialogComponent, {
			data: { code: code },
			id: 'change-org-dialog'
		});
	}

	/**
	 * Sends a request to create a new organization and switch to the new organization.
	 *
	 * This method takes organization data (`createOrgData`) and makes an API call to create a verified organization.
	 * If the organization is created successfully, a message is displayed via a Snackbar. If the creation fails,
	 * an error message is displayed.
	 *
	 * @param {CreateOrganizationsDto} createOrgData - The data needed to create the organization.
	 */
	public createOrganization(createOrgData: CreateOrganizationsDto): void {
		this.apiOrganizationsService.createVerifiedOrganization$Response({ body: createOrgData }).subscribe({
			next: (createResponse) => {
				const orgId = createResponse.body?.body?.data?.id;
				if (orgId) {
					this.changeOrganization(orgId);
				}
				this.snackBarService.open({
					message: this.translocoService.translate('cloud.shared.switch-org.organization-created'),
					actionText: this.translocoService.translate('cloud.shared.button-text.ok')
				});
			},
			error: (error) => {
				const errorMessage = error.error;
				this.snackBarService.open({
					message: this.translocoService.translate(
						errorMessage?.i18nKey
							? 'cloud.shared.error-labels.' + errorMessage.i18nKey
							: errorMessage.message
					),
					actionText: this.translocoService.translate('common.snackbar.ok')
				});
			}
		});
	}

	/**
	 * Changes the current organization session to the specified organization ID.
	 *
	 * This function sets the Okta session tenant to the given organization ID and reloads the window.
	 * The session is set through a service call to `setOktaSessionTenant$`.
	 *
	 * @param {string} organizationId - The ID of the organization to switch to.
	 */
	public changeOrganization(organizationId: string): void {
		this.oktaIntfService
			.setOktaSessionTenant$(organizationId, true)
			.pipe(
				map(() => true),
				catchError(() => {
					return of(false);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: (result: boolean) => {
					if (!result) {
						// if we can't change the tenant, not much we can do. I'm reloading the
						// page to make sure we're still logged in.
						window.location.reload();
					} else {
						this.ngHttpCachingService.clearCacheByRegex(/\/permissions/);
						// If the orgId query parameter is in the URL, reload the page with the new orgId value.
						// Otherwise, we'll reload the page with the parameter removed, merging other parameters.
						const existingOrgIdQueryParameter = this.activatedRoute.snapshot.queryParamMap.get('orgId');
						this.router
							.navigate([], {
								queryParams: {
									orgId: existingOrgIdQueryParameter !== null ? organizationId : null
								},
								queryParamsHandling: 'merge'
							})
							.then(() => {
								window.location.reload();
							});
					}
				}
			});
	}

	/**
	 * Retrieves the unique list of companies associated with the current user.
	 * This method makes an API call to get a list of companies that the current user is associated with.
	 */
	public getMyCompanies(): Observable<StrictHttpResponse<GetMyCompaniesResponse>> {
		return this.apiOrganizationsService.getMyUniqueCompanies$Response();
	}

	/**
	 * Retrieves the unique list of companies associated with the current user.
	 * This method makes an API call to get a list of companies that the current user is associated with.
	 */
	public getAllMyOrganizations(): Observable<StrictHttpResponse<OrganizationList>> {
		return this.apiOrganizationsService.getAllMyOrganizations$Response();
	}

	public getAllMyOrganizationIds$(): Observable<string[]> {
		return this.getAllMyOrganizations().pipe(
			map((orgData) => {
				const items = orgData.body.body?.items;
				if (!items) {
					return [];
				}
				return items.map((item) => item.id ?? '');
			}),
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.loading-orgs-failure'));
				return of([]);
			})
		);
	}

	/**
	 * Get re initiate add licenses flow
	 * @returns reInitiateAddLicenses - boolean representation of re initiate add licenses flow
	 */
	public getReInitiateAddLicenses(): Signal<boolean> {
		return computed(() => this.reInitiateAddLicenses());
	}

	/**
	 * Set re initiate add licenses flow
	 *  * @param reInitiate - boolean representation of re initiate add licenses flow
	 */
	public setReInitiateAddLicenses(reInitiate: boolean): void {
		this.reInitiateAddLicenses.set(reInitiate);
	}

	// checks for the existence of the OrgId query parameter and if present
	// will see if the user's org needs to be switched.
	// returns a boolean observer so callers know when the work is done

	public handleOrgIdQueryParameter(): Observable<boolean> {
		const requestedOrgId$ = this.activatedRoute.queryParamMap.pipe(
			map((queryParamMap) => queryParamMap.get('orgId')),
			map((orgId) => orgId?.replace(/["']/g, '') ?? '') // remove leading/training quotes
		);
		const currentOrgId$ = this.oktaIntfService.getUserProfile$().pipe(map((userProfile) => userProfile.orgId));
		const allMyOrgIds$ = this.getAllMyOrganizationIds$();

		return combineLatest([requestedOrgId$, currentOrgId$, allMyOrgIds$]).pipe(
			concatMap(([requestedOrgId, currentOrgId, allOrgIds]) => {
				return this.checkAndSwitchOrgs(requestedOrgId, currentOrgId, allOrgIds);
			}),
			takeUntilDestroyed(this.destroyRef)
		);
	}

	private checkAndSwitchOrgs(requestedOrgId: string, currentOrgId: string, allOrgIds: string[]): Observable<boolean> {
		// no requested org, can't switch
		if (!requestedOrgId || requestedOrgId.length === 0) {
			return of(true);
		}

		// already on this org... no switching is necessary.
		if (requestedOrgId === currentOrgId) {
			return of(true);
		}

		// if the requested Org Id is not a valid org for the user put up a snackbar,
		// then remove the orgId parameter from the URL by routing to '/' w/out the query parameter
		if (!allOrgIds.includes(requestedOrgId)) {
			this.displaySnackbarError(asLabel('cloud.shared.switch-org.not-a-member-of-org'));
			// Normally, we don't want to muck with the query parameters, but since it is not valid
			// route to / to clean up the URL.
			this.router.navigate(['/']).then();
			return of(true);
		}

		const result = this.oktaIntfService.setOktaSessionTenant$(requestedOrgId, false).pipe(
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.okta-tenant-update-failure'));
				return of(true);
			}),
			concatMap(() => {
				// Instead of refreshing the page, reload the account info.  This will trigger the update
				// of account/user info throughout the app, w/out the page reload.
				return this.reloadAccountInfo();
			})
		);

		return result;
	}

	private reloadAccountInfo(): Observable<boolean> {
		return this.apiUsersService.getSessionAccountInfo$Response().pipe(
			tap((accountInfoResp) => {
				if (accountInfoResp?.body?.body) {
					this.orgStoreService.setAccountInfo(accountInfoResp.body.body);
				}
			}),
			map(() => true),
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.reload-account-failure'));
				return of(true);
			})
		);
	}

	private displaySnackbarError(key: string): void {
		this.snackBarService.open({
			message: this.translocoService.translate(key),
			actionText: this.translocoService.translate('common.snackbar.dismiss')
		});
	}
}
